<script setup lang="ts">
import type { Category, Content, SiteStats, Tag } from '~/types/blog'
import { normalizeFieldBool, normalizeFieldNumber, normalizeFieldText } from '~/composables/useCustomFields'
import { resolveAvatarUrl } from '~/composables/useAvatar'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { buildContentPathFromComment, buildPostPath } from '~/composables/usePermalink'

const props = defineProps<{
  categories: Category[]
  tags: Tag[]
}>()

const blogApi = useBlogApi()
const siteData = await useSiteData()
const fields = siteData?.setting?.customFields

const showAuthor = computed(() => normalizeFieldBool(fields, 'showAuthorCard', true))
const authorName = computed(() => normalizeFieldText(fields, 'authorName', '站长'))
const authorBio = computed(() => normalizeFieldText(fields, 'authorBio', ''))
const authorAvatar = computed(() => normalizeFieldText(fields, 'authorAvatar', ''))
const authorEmail = computed(() => normalizeFieldText(fields, 'authorEmail', ''))
const gravatarMirror = computed(() => normalizeFieldText(fields, 'gravatarMirror', ''))

const showNotice = computed(() => normalizeFieldBool(fields, 'showNotice', true))
const noticeTitle = computed(() => normalizeFieldText(fields, 'noticeTitle', '公告'))
const noticeContent = computed(() => normalizeFieldText(fields, 'noticeContent', ''))
const noticeLines = computed(() =>
  String(noticeContent.value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
)

const showLatestComments = computed(() => normalizeFieldBool(fields, 'showLatestComments', true))
const latestCommentsTitle = computed(() => normalizeFieldText(fields, 'latestCommentsTitle', '最新评论'))
const latestCommentsLimit = computed(() => Math.min(20, Math.max(1, normalizeFieldNumber(fields, 'latestCommentsLimit', 8))))
const showSidebarHotPosts = computed(() => normalizeFieldBool(fields, 'showSidebarHotPosts', false))
const sidebarHotPostsTitle = computed(() => normalizeFieldText(fields, 'sidebarHotPostsTitle', '热门文章'))
const sidebarHotPostsLimit = computed(() => Math.min(10, Math.max(1, normalizeFieldNumber(fields, 'sidebarHotPostsLimit', 5))))

const showSiteStats = computed(() => normalizeFieldBool(fields, 'showSiteStats', true))
const siteStatsTitle = computed(() => normalizeFieldText(fields, 'siteStatsTitle', '站点统计'))

const showTagCloud = computed(() => normalizeFieldBool(fields, 'showTagCloud', true))
const tagCloudTitle = computed(() => normalizeFieldText(fields, 'tagCloudTitle', '标签云'))
const tagCloudLimit = computed(() => Math.min(80, Math.max(8, normalizeFieldNumber(fields, 'tagCloudLimit', 30))))
const sidebarSectionOrder = computed(() => ({
  author: Math.floor(normalizeFieldNumber(fields, 'sidebarAuthorOrder', 10)),
  notice: Math.floor(normalizeFieldNumber(fields, 'sidebarNoticeOrder', 20)),
  hotPosts: Math.floor(normalizeFieldNumber(fields, 'sidebarHotPostsOrder', 30)),
  latestComments: Math.floor(normalizeFieldNumber(fields, 'sidebarLatestCommentsOrder', 40)),
  siteStats: Math.floor(normalizeFieldNumber(fields, 'sidebarSiteStatsOrder', 50)),
  tagCloud: Math.floor(normalizeFieldNumber(fields, 'sidebarTagCloudOrder', 60))
}))

const { data: latestComments } = await useAsyncData(
  () => `sidebar-latest-comments:${showLatestComments.value ? 'on' : 'off'}:${latestCommentsLimit.value}`,
  async () => {
    if (!showLatestComments.value) {
      return []
    }
    return blogApi.getLatestComments(latestCommentsLimit.value)
  },
  { watch: [showLatestComments, latestCommentsLimit] }
)
const { data: hotPosts } = await useAsyncData(
  () => `sidebar-hot-posts:${showSidebarHotPosts.value ? 'on' : 'off'}:${sidebarHotPostsLimit.value}`,
  async () => {
    if (!showSidebarHotPosts.value) {
      return [] as Content[]
    }
    return blogApi.getHotContentList({ limit: sidebarHotPostsLimit.value })
  },
  { watch: [showSidebarHotPosts, sidebarHotPostsLimit] }
)
const { data: siteStats } = await useAsyncData<SiteStats | null>(
  () => `sidebar-site-stats:${showSiteStats.value ? 'on' : 'off'}`,
  async () => {
    if (!showSiteStats.value) {
      return null
    }
    return blogApi.getSiteStats()
  },
  { watch: [showSiteStats] }
)

const avatarSrc = computed(() => {
  if (authorAvatar.value) {
    return resolveImageAssetUrl(authorAvatar.value)
  }
  return resolveAvatarUrlFromEmail({ email: authorEmail.value, name: authorName.value }, gravatarMirror.value)
})

const latestCommentList = computed(() => (latestComments.value || []).slice(0, latestCommentsLimit.value))
const hotPostList = computed(() => (hotPosts.value || []).slice(0, sidebarHotPostsLimit.value))
const resolveCommentLink = (item: { contentType?: string; contentSlug?: string; contentId?: number; commentPath?: string }) =>
  buildContentPathFromComment(fields, item)
const resolveHotPostLink = (item: Content) => buildPostPath(fields, item, { category: item.categories?.[0]?.slug || '' })
const getSidebarOrderStyle = (key: keyof typeof sidebarSectionOrder.value) => ({ order: sidebarSectionOrder.value[key] })
const formatHotPostRank = (index: number) => String(index + 1)
</script>

<template>
  <div class="sidebar-group">
    <section v-if="showAuthor" class="card block author-card" :style="getSidebarOrderStyle('author')">
      <BlogImage :src="avatarSrc" :alt="authorName" />
      <div>
        <h3>{{ authorName }}</h3>
        <p>{{ authorBio }}</p>
      </div>
    </section>

    <section v-if="showNotice" class="card block notice-card" :style="getSidebarOrderStyle('notice')">
      <h3><i class="fa-regular fa-bell" aria-hidden="true" />{{ noticeTitle }}</h3>
      <ul v-if="noticeLines.length" class="notice-list">
        <li v-for="(line, idx) in noticeLines" :key="idx">{{ line }}</li>
      </ul>
      <div v-else class="empty-card">暂无公告</div>
    </section>

    <section v-if="showLatestComments" class="card block" :style="getSidebarOrderStyle('latestComments')">
      <h3><i class="fa-regular fa-comment-dots" aria-hidden="true" />{{ latestCommentsTitle }}</h3>
      <ul v-if="latestCommentList.length" class="comment-list">
        <li v-for="item in latestCommentList" :key="item.id">
          <div class="comment-item-link">
            <BlogImage :src="resolveAvatarUrl(item.emailHash, gravatarMirror)" :alt="item.author" />
            <div class="comment-text">
              <p class="comment-head">
                <span class="comment-author">{{ item.author || '匿名用户' }}</span>
                <span class="comment-verb">评论</span>
                <NuxtLink :to="resolveCommentLink(item)" class="comment-post-link" :title="item.contentTitle || '未知文章'">
                  {{ item.contentTitle || '未知文章' }}
                </NuxtLink>
              </p>
              <BlogUiTooltip :content="item.content || ''">
                <p class="comment-main">{{ item.content || '（无内容）' }}</p>
              </BlogUiTooltip>
            </div>
          </div>
        </li>
      </ul>
      <div v-else class="empty-card">暂无评论</div>
    </section>

    <section v-if="showSidebarHotPosts && hotPostList.length" class="card block" :style="getSidebarOrderStyle('hotPosts')">
      <h3><i class="fa-regular fa-star" aria-hidden="true" />{{ sidebarHotPostsTitle }}</h3>
      <ol class="hot-post-list">
        <li v-for="(item, index) in hotPostList" :key="item.ID">
          <NuxtLink :to="resolveHotPostLink(item)" class="hot-post-link">
            <span class="hot-post-rank">
              <strong class="hot-post-rank-number">{{ formatHotPostRank(index) }}</strong>
            </span>
            <span class="hot-post-body">
              <span class="hot-post-title">{{ item.title || '未命名文章' }}</span>
              <span class="hot-post-meta">
                <span><i class="fa-regular fa-eye" aria-hidden="true" />{{ item.viewCount || 0 }}</span>
                <span><i class="fa-regular fa-heart" aria-hidden="true" />{{ item.likeCount || 0 }}</span>
                <span><i class="fa-regular fa-comment" aria-hidden="true" />{{ item.commentCount || 0 }}</span>
              </span>
            </span>
          </NuxtLink>
        </li>
      </ol>
    </section>

    <section v-if="showSiteStats" class="card block" :style="getSidebarOrderStyle('siteStats')">
      <h3><i class="fa-regular fa-chart-bar" aria-hidden="true" />{{ siteStatsTitle }}</h3>
      <ul class="simple-list">
        <li><span>标签</span><strong>{{ siteStats?.tagCount || 0 }}</strong></li>
        <li><span>文章</span><strong>{{ siteStats?.postCount || 0 }}</strong></li>
        <li><span>页面</span><strong>{{ siteStats?.pageCount || 0 }}</strong></li>
        <li><span>评论</span><strong>{{ siteStats?.commentCount || 0 }}</strong></li>
        <li><span>分类</span><strong>{{ siteStats?.categoryCount || 0 }}</strong></li>
        <li><span>更新</span><strong>{{ siteStats?.lastUpdated || '-' }}</strong></li>
      </ul>
    </section>

    <section v-if="showTagCloud" class="card block" :style="getSidebarOrderStyle('tagCloud')">
      <h3><i class="fa-regular fa-bookmark" aria-hidden="true" />{{ tagCloudTitle }}</h3>
      <div class="tags-wrap">
        <NuxtLink
          v-for="tag in (props.tags || []).slice(0, tagCloudLimit)"
          :key="tag.ID"
          :to="`/tag/${encodeURIComponent(tag.slug || tag.name)}`"
          class="tag-item"
        >
          #{{ tag.name }} <span>{{ tag.contentCount || 0 }}</span>
        </NuxtLink>
      </div>
    </section>
  </div>
</template>

<style scoped>
.sidebar-group {
  display: grid;
  gap: 18px;
}

.block {
  padding: 16px;
}

.block h3 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: var(--card-title-size);
}

.author-card {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.author-card img {
  width: 56px;
  height: 56px;
  border: 1px solid var(--line);
  border-radius: 999px;
  object-fit: cover;
}

.author-card p {
  font-size: 13px;
  color: var(--text-soft);
}

.notice-list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;
}

.notice-list li {
  font-size: 13px;
  color: var(--text-soft);
}

.comment-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
}

.comment-item-link {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.comment-item-link img {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid var(--line);
}

.comment-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.comment-head {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  font-size: 12px;
}

.comment-author,
.comment-verb {
  flex: 0 0 auto;
  color: var(--text-soft);
}

.comment-author {
  color: var(--text);
  font-weight: 600;
}

.comment-post-link {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  text-decoration: none;
}

.comment-post-link:hover {
  color: var(--brand);
  text-decoration: underline;
}

.comment-main {
  margin: 5px 0 0;
  color: var(--text);
  font-size: 13px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hot-post-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
}

.hot-post-link {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  color: inherit;
  text-decoration: none;
}

.hot-post-link:hover .hot-post-title {
  color: var(--brand);
}

.hot-post-rank {
  align-items: center;
  background: color-mix(in srgb, var(--brand) 10%, transparent);
  border-radius: 4px;
  color: var(--brand);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  height: 20px;
  justify-content: center;
  width: 20px;
}

.hot-post-rank-number {
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
}

.hot-post-body {
  min-width: 0;
  display: grid;
  gap: 5px;
}

.hot-post-title {
  display: -webkit-box;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hot-post-meta {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: var(--text-soft);
}

.hot-post-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 10px;
  vertical-align: top;
}

.hot-post-meta span:last-child {
  margin-right: 0;
}

.simple-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
}

.simple-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.simple-list span {
  color: var(--text-soft);
  font-size: 13px;
}

.simple-list strong {
  font-size: 13px;
}

.tags-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-item {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 4px 8px;
  border: 0;
  border-radius: 999px;
  color: var(--text-soft);
  background: var(--bg);
  text-decoration: none;
  font-size: 12px;
}

.tag-item span {
  color: var(--text-soft);
  font-size: 11px;
}

@media (max-width: 960px) {
  .sidebar-group {
    gap: 0;
  }

  .sidebar-group > :not(.notice-card) {
    display: none;
  }
}
</style>
